<script>
  import { createEventDispatcher } from "svelte";
  import Modal from "@components/Modal.svelte";
  import ExcelFileIcon from "@components/common/icons/app/ExcelFileIcon.svelte";
  import InfoIcon from "@components/common/icons/app/InfoIcon.svelte";
  import { Select, Button } from "paxray-components-svelte";
  import * as XLSX from "xlsx";
  import { DataTable } from "carbon-components-svelte";
  import { ARROW_LEFT_DARK_ICON, CLOSE_ICON, DOWNLOAD_ICON, UPLOAD_ICON } from '@utils/icons.js';

  export let open = false;
  export let loading = false;
  export let maxFileSizeMb = 25;
  export let acceptedTypes = [".xlsx", ".xls"];
  export let heading = "Import Team Users";
  export let projectName = "Project";
  export let includeTeam = true;
  export let existingUsersOnly = false;

  const dispatch = createEventDispatcher();

  // File/dropzone state for the upload step.
  let files = [];
  let dropzone;

  // Step state. The sheet-selection step only exists for files with multiple sheets.
  let currentStep = 1;
  let dropError = "";
  let tableLoading = false;

  // Parsed Excel state used by the mapping and preview steps.
  let excelColumns = [];
  let previewByColumn = {};
  let parsedRows = [];
  let previewRows = [];
  let duplicateLoginRows = [];
  let mappedRowsForDuplicateCheck = [];
  let showDuplicateLoginStep = false;

  // Workbook/sheet state. The template sheet is preselected if it exists.
  let workbook = null;
  let sheetOptions = [];
  let selectedSheetName = "";
  let missingTemplateHeaders = [];

  // The same user fields are shared by the team and admin import variants.
  const userFields = [
    { key: "name", label: "System Login Name", required: true },
    { key: "first_name", label: "First Name", required: false },
    { key: "last_name", label: "Last Name", required: false },
    { key: "email", label: "E-Mail", required: false },
  ];
  $: requiredFields = includeTeam
    ? [{ key: "team", label: "Team", required: true }, ...userFields]
    : userFields;
  $: templateHeaders = [
    ...(includeTeam ? ["Team"] : []),
    "First Name",
    "Last Name",
    "System Login Name",
    "E-Mail",
  ];
  const defaultSheetName = "3. Client Rollout Users";

  // Maps each required payload key to the selected Excel column header.
  let columnMapping = {
    team: "",
    first_name: "",
    last_name: "",
    name: "",
    email: "",
  };

  $: requiredMappingsValid = requiredFields
    .filter((field) => field.required)
    // .every((field) => columnMapping[field.key]);
    .every((field) => {
      const value = columnMapping[field.key];
      return value && String(value).trim() !== "";
    });

  $: previewHeaders = requiredFields
    .filter((field) => columnMapping[field.key])
    .map((field) => ({ key: field.key, value: field.label }));

  $: previewTableRows = previewRows.map((row, i) => {
    const mapped = { id: String(i) };
    for (const field of requiredFields) {
      if (columnMapping[field.key]) {
        mapped[field.key] = row[field.key] || "";
      }
    }
    return mapped;
  });

  $: duplicateLoginTableRows = duplicateLoginRows.map((row, i) => ({
    id: String(i),
    rowNumber: row.rowNumber,
    team: row.team,
    first_name: row.first_name,
    last_name: row.last_name,
    name: row.name,
    email: row.email,
  }));
  $: duplicateLoginHeaders = [
    { key: "rowNumber", value: "Row" },
    ...(includeTeam ? [{ key: "team", value: "Team" }] : []),
    { key: "name", value: "System Login Name" },
    { key: "first_name", value: "First Name" },
    { key: "last_name", value: "Last Name" },
    { key: "email", value: "E-Mail" },
  ];

  $: duplicateRowsWithData = duplicateLoginRows.filter(hasImportData);
  $: duplicateLoginIssues = getDuplicateLoginIssues(duplicateRowsWithData);
  $: duplicateLoginValid = duplicateLoginIssues.length === 0;

  $: selectedFile = files?.[0] ?? null;
  $: acceptedFileTypeText = acceptedTypes.join(", ");
  $: hasMultipleSheets = sheetOptions.length > 1;

  // Step numbers shift when the optional sheet-selection step is shown.
  $: mapStep = hasMultipleSheets ? 3 : 2;
  $: previewStep = hasMultipleSheets ? 4 : 3;
  $: primaryButtonDisabled =
    currentStep === 1
      ? loading || !selectedFile
      : currentStep === 2
        ? hasMultipleSheets
          ? !selectedSheetName
          : !requiredMappingsValid
        : currentStep === mapStep
          ? !requiredMappingsValid
          : tableLoading || (showDuplicateLoginStep && !duplicateLoginValid);

  function normalizeFiles(event) {
    dropError = "";
    const detailFiles = event?.detail?.files;
    const inputFiles = event?.target?.files;

    if (Array.isArray(detailFiles)) {
      files = detailFiles;
      return;
    }

    if (inputFiles) {
      files = Array.from(inputFiles);
      return;
    }

    files = [];
  }

  function openNativePicker() {
    const input = dropzone?.querySelector('input[type="file"]');
    input?.click();
  }

  function handleSecondaryClick() {
    files = [];
    open = false;
    dispatch("close");
    resetImport();
  }

  function handleClose() {
    files = [];
    dispatch("close");
    resetImport();
  }

  function resetImport() {
    currentStep = 1;
    workbook = null;
    sheetOptions = [];
    selectedSheetName = "";
    excelColumns = [];
    previewByColumn = {};
    parsedRows = [];
    previewRows = [];
    duplicateLoginRows = [];
    mappedRowsForDuplicateCheck = [];
    showDuplicateLoginStep = false;
    missingTemplateHeaders = [];
    columnMapping = {
      team: "",
      first_name: "",
      last_name: "",
      name: "",
      email: "",
    };
  }

  async function handlePrimaryAction() {
    // Step 1: read the workbook and decide whether sheet selection is needed.
    if (currentStep === 1) {
      if (!selectedFile || loading) return;
      try {
        loading = true;
        await readExcelFile(selectedFile);
        if (sheetOptions.length > 1) {
          currentStep = 2;
        } else {
          parseSelectedSheet();
          currentStep = 2;
        }
      } catch (error) {
        console.error("Error reading Excel file:", error);
      } finally {
        loading = false;
      }
      return;
    }

    // Optional step 2: parse the user-selected worksheet.
    if (hasMultipleSheets && currentStep === 2) {
      if (!selectedSheetName) return;
      parseSelectedSheet();
      currentStep = mapStep;
      return;
    }

    // Mapping step: build a small preview from the selected column mapping.
    if (currentStep === mapStep) {
      if (!requiredMappingsValid) return;
      previewRows = parsedRows.slice(0, 5).map((row) => {
        const mapped = {};
        for (const field of requiredFields) {
          const col = columnMapping[field.key];
          mapped[field.key] = col ? (row[col] ?? "") : "";
        }
        return mapped;
      });
      currentStep = previewStep;
      tableLoading = true;
      setTimeout(() => {
        tableLoading = false;
      }, 1000);
      showDuplicateLoginStep = false;
      return;
    }

    // Final step: emit all mapped rows. Backend persistence is handled by TeamSettings.
    const mappedRows = parsedRows.map((row) => {
      const mapped = {};
      for (const field of requiredFields) {
        const col = columnMapping[field.key];
        if (col) mapped[field.key] = (row[col] ?? "").toString().trim();
      }
      return mapped;
    });

    const duplicates = getDuplicateLoginRows(mappedRows);
    if (duplicates.length && !showDuplicateLoginStep) {
      mappedRowsForDuplicateCheck = mappedRows;
      duplicateLoginRows = duplicates;
      showDuplicateLoginStep = true;
      return;
    }

    if (showDuplicateLoginStep && !duplicateLoginValid) return;

    if (showDuplicateLoginStep) {
      const correctedRows = mergeDuplicateCorrections(
        mappedRowsForDuplicateCheck,
      );
      const remainingDuplicates = getDuplicateLoginRows(correctedRows);

      if (remainingDuplicates.length) {
        mappedRowsForDuplicateCheck = correctedRows;
        duplicateLoginRows = remainingDuplicates;
        return;
      }

      dispatch("submit", {
        rows: correctedRows,
      });
      return;
    }

    dispatch("submit", {
      rows: mappedRows,
    });
  }

  function handleBack() {
    if (showDuplicateLoginStep) {
      showDuplicateLoginStep = false;
      return;
    }

    if (currentStep > 1) {
      currentStep -= 1;
    } else if (currentStep === 1) {
      files = [];
      const input = dropzone?.querySelector('input[type="file"]');
      if (input) input.value = "";
    }
  }

  function normalizeLoginName(value) {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }

  function hasImportData(row) {
    return requiredFields.some((field) => String(row[field.key] ?? "").trim());
  }

  function getDuplicateLoginRows(rows) {
    const counts = rows.reduce((result, row) => {
      const loginName = normalizeLoginName(row.name);
      if (!loginName) return result;
      result[loginName] = (result[loginName] || 0) + 1;
      return result;
    }, {});

    return rows
      .map((row, index) => ({
        ...row,
        sourceIndex: index,
        rowNumber: index + 2,
      }))
      .filter((row) => {
        const loginName = normalizeLoginName(row.name);
        return !loginName || counts[loginName] > 1;
      });
  }

  function mergeDuplicateCorrections(rows) {
    const correctionsByIndex = Object.fromEntries(
      duplicateLoginRows.map((row) => [row.sourceIndex, row]),
    );

    return rows.map((row, index) => {
      const correction = correctionsByIndex[index];
      if (!correction) return row;

      return {
        team: correction.team,
        first_name: correction.first_name,
        last_name: correction.last_name,
        name: correction.name,
        email: correction.email,
      };
    });
  }

  function updateDuplicateLoginRow(rowId, key, value) {
    const rowIndex = Number(rowId);

    duplicateLoginRows = duplicateLoginRows.map((row, index) =>
      index === rowIndex ? { ...row, [key]: value } : row,
    );
  }

  function isDuplicateLoginInputInvalid(row) {
    const loginName = normalizeLoginName(row.name);
    if (!loginName) return true;

    return (
      duplicateRowsWithData.filter(
        (duplicateRow) => normalizeLoginName(duplicateRow.name) === loginName,
      ).length > 1
    );
  }

  function getDuplicateLoginIssues(rows) {
    const rowsWithData = rows.filter(hasImportData);
    const missingRows = rowsWithData.filter(
      (row) => !normalizeLoginName(row.name),
    );
    const rowsByLoginName = rowsWithData.reduce((result, row) => {
      const loginName = normalizeLoginName(row.name);
      if (!loginName) return result;
      result[loginName] = [...(result[loginName] || []), row];
      return result;
    }, {});

    return [
      ...missingRows.map((row) => ({
        type: "missing",
        message: `Column "System Login Name" is required in row ${row.rowNumber}.`,
      })),
      ...Object.entries(rowsByLoginName)
        .filter(([, duplicateRows]) => duplicateRows.length > 1)
        .map(([loginName, duplicateRows]) => ({
          type: "duplicate",
          message: `Column "System Login Name" must be unique: "${loginName}" is used in rows ${duplicateRows
            .map((row) => row.rowNumber)
            .join(", ")}.`,
        })),
    ];
  }

  function downloadDuplicateLoginRows() {
    const rows = duplicateLoginRows.map((row) => ({
      Row: row.rowNumber,
      ...(includeTeam ? { Team: row.team } : {}),
      "First Name": row.first_name,
      "Last Name": row.last_name,
      "System Login Name": row.name,
      "E-Mail": row.email,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Login Name Conflicts");
    XLSX.writeFile(
      workbook,
      `Name_Conflicts_Paxray_${sanitizeFilePart(projectName)}_${getDateStamp()}.xlsx`,
    );
  }

  function skipDuplicateLoginRowsAndSubmit() {
    const skippedIndexes = new Set(
      duplicateLoginRows.map((row) => row.sourceIndex),
    );
    const rowsToImport = mappedRowsForDuplicateCheck.filter(
      (_, index) => !skippedIndexes.has(index),
    );

    downloadDuplicateLoginRows();
    dispatch("submit", {
      rows: rowsToImport,
    });
  }

  function removeSelectedFile(event) {
    event?.stopPropagation();
    files = [];
    dropError = "";
    const input = dropzone?.querySelector('input[type="file"]');
    if (input) input.value = "";
  }

  function sanitizeFilePart(value) {
    return (
      String(value || "Project")
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "") || "Project"
    );
  }

  function getDateStamp() {
    return new Date().toISOString().slice(0, 10);
  }

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDrop(event) {
    event.preventDefault();
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles?.length) {
      const file = droppedFiles[0];
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!acceptedTypes.includes(ext)) {
        dropError = `File type "${ext}" is not allowed. Accepted: ${acceptedFileTypeText}`;
        files = [];
        return;
      }
      dropError = "";
      files = Array.from(droppedFiles);
    }
  }

  async function readExcelFile(file) {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: "array" });
    sheetOptions = workbook.SheetNames.map((sheetName) => ({
      value: sheetName,
      text: sheetName,
    }));
    selectedSheetName = workbook.SheetNames.includes(defaultSheetName)
      ? defaultSheetName
      : workbook.SheetNames[0] || "";
  }

  function parseSelectedSheet() {
    const worksheet = workbook?.Sheets?.[selectedSheetName];
    if (!worksheet) {
      parsedRows = [];
      excelColumns = [];
      previewByColumn = {};
      return;
    }

    const rawRows = XLSX.utils
      .sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: false,
      })
      .filter((row) => row.some((value) => String(value ?? "").trim() !== ""));

    if (!rawRows.length) {
      parsedRows = [];
      excelColumns = [];
      previewByColumn = {};
      return;
    }

    const firstRow = rawRows[0].map((value) => String(value ?? "").trim());

    // Prefer the exact company template headers. This keeps "System Login Name"
    // from accidentally mapping to "First Name" or "Last Name".
    missingTemplateHeaders = templateHeaders.filter(
      (templateHeader) => !firstRow.includes(templateHeader),
    );
    const hasTemplateHeaders = missingTemplateHeaders.length === 0;

    // If the exact template is not present, still support slightly different files.
    const hasHeader =
      hasTemplateHeaders ||
      firstRow.some((value) =>
        ["team", "first name", "last name", "system login name", "e mail"].some(
          (pattern) => normalizeHeader(value).includes(pattern),
        ),
      );
    const fallbackHeaders = templateHeaders;

    // Headerless files are interpreted in template column order.
    const headers = hasHeader
      ? firstRow
      : firstRow.map(
          (_, index) => fallbackHeaders[index] || `Column ${index + 1}`,
        );
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;

    parsedRows = dataRows.map((row) =>
      Object.fromEntries(
        headers.map((header, index) => [
          header,
          String(row[index] ?? "").trim(),
        ]),
      ),
    );

    excelColumns = headers.map((header) => ({
      value: header,
      text: header,
    }));

    previewByColumn = Object.fromEntries(
      headers.map((header) => {
        const first3 = parsedRows
          .map((row) => row[header])
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).trim() !== "",
          )
          .slice(0, 3)
          .map((value) => String(value).trim());

        return [header, first3];
      }),
    );

    columnMapping = buildInitialMapping(headers);
  }

  // Normalizes user-provided headers for tolerant fallback matching.
  function normalizeHeader(value) {
    return String(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function buildInitialMapping(headers) {
    // First try exact template labels, then use tolerant aliases.
    const findExactHeader = (label) =>
      headers.find((header) => header === label) || "";
    const normalizedHeaders = headers.map((header) => ({
      original: header,
      normalized: normalizeHeader(header),
    }));

    const findHeader = (patterns, excludePatterns = []) => {
      const match = normalizedHeaders.find(
        ({ normalized }) =>
          patterns.some((pattern) => normalized.includes(pattern)) &&
          !excludePatterns.some((pattern) => normalized.includes(pattern)),
      );
      return match?.original ?? "";
    };

    return {
      team: findExactHeader("Team") || findHeader(["team"]),
      first_name:
        findExactHeader("First Name") ||
        findHeader(["first name", "firstname", "given name"]),
      last_name:
        findExactHeader("Last Name") ||
        findHeader(["last name", "lastname", "surname"]),
      name:
        findExactHeader("System Login Name") ||
        findHeader([
          "system login name",
          "login name",
          "system name",
          "user id",
          "userid",
        ]),
      email:
        findExactHeader("E-Mail") || findHeader(["e mail", "email", "mail"]),
    };
  }
</script>

<Modal
  bind:open
  actions
  autowidth
  modalClass="import-team-users-modal-shell"
  preventCloseOnClickOutside
  modalHeading={heading}
  primaryButtonText={currentStep === previewStep
    ? showDuplicateLoginStep
      ? "Continue"
      : "Import"
    : "Next"}
  secondaryButtonText="Cancel"
  primaryButtonAction={handlePrimaryAction}
  primaryButtonNotAllow={primaryButtonDisabled}
  primaryButtonValidations={primaryButtonDisabled}
  on:click:button--secondary={handleSecondaryClick}
  on:close={handleClose}
>
  <div class="import-team-users-modal">
    <div
      class="steps"
      class:steps--multiple-sheets={hasMultipleSheets}
      aria-label="Import steps"
    >
      <div
        class="step"
        class:step--active={currentStep === 1}
        class:step--done={currentStep > 1}
      >
        <span class="step__bullet">{currentStep > 1 ? "✓" : "1"}</span>
        <span class="step__label">Upload File</span>
      </div>

      <div class="step__line" aria-hidden="true"></div>

      {#if hasMultipleSheets}
        <div
          class="step"
          class:step--active={currentStep === 2}
          class:step--done={currentStep > 2}
        >
          <span class="step__bullet">{currentStep > 2 ? "✓" : "2"}</span>
          <span class="step__label">Select Sheet</span>
        </div>

        <div class="step__line" aria-hidden="true"></div>
      {/if}

      <div
        class="step"
        class:step--active={currentStep === mapStep}
        class:step--done={currentStep > mapStep}
      >
        <span class="step__bullet">{currentStep > mapStep ? "✓" : mapStep}</span
        >
        <span class="step__label">Map Columns</span>
      </div>

      <div class="step__line" aria-hidden="true"></div>

      <div class="step" class:step--active={currentStep === previewStep}>
        <span class="step__bullet">{previewStep}</span>
        <span class="step__label">Preview & Import</span>
      </div>
    </div>

    {#if currentStep === 1}
      <div class="copy-block">
        <h3>Upload your Excel file</h3>
        <p>
          Upload an Excel file (.xlsx) that contains {includeTeam
            ? "team and user data"
            : "client information"}.
        </p>
        {#if existingUsersOnly}
          <p>
            Only existing sensor users are updated. The System Login Name is
            used for matching and is never changed.
          </p>
        {/if}
      </div>

      <div
        class="dropzone-shell"
        bind:this={dropzone}
        on:dragover|preventDefault={handleDragOver}
        on:drop|preventDefault={handleDrop}
      >
        <input
          type="file"
          accept={acceptedTypes.join(",")}
          hidden
          on:change={normalizeFiles}
        />
        <div class="dropzone-overlay">
          {#if loading}
            <p class="dropzone-title">Uploading</p>
            <p class="dropzone-separator">please wait</p>
          {:else if selectedFile}
            <div class="selected-file-header">
              <Button
                iconDescription="Remove selected file"
                on:click={removeSelectedFile}
                icon={CLOSE_ICON}
                size="small"
                rounded
                variant="secondary"
              />
            </div>
            <div class="excel-icon">
              <ExcelFileIcon size={52} />
            </div>
            <p class="dropzone-filename">{selectedFile.name}</p>
          {:else}
            <ExcelFileIcon size={52} />
            <p class="dropzone-title">Drag and drop your file here</p>
            <p class="dropzone-separator">or</p>
            <Button
              size="field"
              kind="secondary"
              text="Choose File"
              iconLeft
              icon={UPLOAD_ICON}
              on:click={openNativePicker}
            />
          {/if}
        </div>
      </div>
      {#if dropError}
        <p class="drop-error">{dropError}</p>
      {/if}

      <div class="meta-text">
        <span>Accepted file type: {acceptedFileTypeText}</span>
        <span>Maximum file size: {maxFileSizeMb} MB</span>
      </div>
    {:else if hasMultipleSheets && currentStep === 2}
      <div class="copy-block">
        <h3>Select worksheet</h3>
        <p>
          This file contains multiple worksheets. Select the sheet that contains
          the team and user data.
        </p>
      </div>

      <div class="sheet-select">
        <Select
          items={sheetOptions}
          bind:selected={selectedSheetName}
          placeholder="Select worksheet"
        />
      </div>

      <div class="info-box">
        <InfoIcon size={18} />
        Only the selected worksheet will be used for column mapping and preview.
      </div>
    {:else if currentStep === mapStep}
      <div class="copy-block">
        <h3>Map Excel columns to required fields</h3>
        <p>
          Select which columns from your file correspond to the required fields
          below.
        </p>
      </div>

      <div class="mapping-table">
        <div class="mapping-table__head">
          <div>Required Field</div>
          <div>Map to Excel Column</div>
          <div>Preview (first 3 rows)</div>
        </div>

        {#each requiredFields as field}
          <div class="mapping-table__row">
            <div class="mapping-table__field">
              {field.label}{field.required ? " *" : ""}
            </div>

            <div>
              <Select
                items={excelColumns}
                bind:selected={columnMapping[field.key]}
                placeholder="Select column"
              />
            </div>

            <div class="mapping-table__preview">
              {#if columnMapping[field.key]}
                {#each previewByColumn[columnMapping[field.key]] || [] as previewValue}
                  <div>{previewValue}</div>
                {/each}
              {:else}
                —
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <p class="required-note">* Required fields</p>

      <div class="info-box">
        <InfoIcon size={18} />
        {#if missingTemplateHeaders.length}
          Missing exact template column(s): {missingTemplateHeaders.join(", ")}.
          Fallback mapping was applied where possible.
        {:else}
          All template columns were found exactly.
        {/if}
      </div>
    {:else if currentStep === previewStep}
      {#if showDuplicateLoginStep}
        <div class="copy-block">
          <h3>Duplicate or missing System Login Names detected</h3>
          <p>
            Every System Login Name must be filled and unique. Correct the
            values before continuing. Excluded rows will not be imported and
            will be downloaded as an Excel file for correction.
          </p>
        </div>

        <div class="preview-table-wrapper">
          <DataTable
            class="custom-table zebra-rows"
            headers={duplicateLoginHeaders}
            rows={duplicateLoginTableRows}
          >
            <svelte:fragment slot="cell" let:cell let:row>
              {#if ["team", "first_name", "last_name", "name", "email"].includes(cell.key)}
                <div
                  class:login-cell-state={cell.key === "name"}
                  class:login-cell-state--error={cell.key === "name" &&
                    isDuplicateLoginInputInvalid(
                      duplicateLoginRows[Number(row.id)],
                    )}
                  class:login-cell-state--valid={cell.key === "name" &&
                    !isDuplicateLoginInputInvalid(
                      duplicateLoginRows[Number(row.id)],
                    )}
                >
                  <input
                    class:input-error={cell.key === "name" &&
                      isDuplicateLoginInputInvalid(
                        duplicateLoginRows[Number(row.id)],
                      )}
                    value={cell.value}
                    on:input={(event) => {
                      updateDuplicateLoginRow(
                        row.id,
                        cell.key,
                        event.currentTarget.value,
                      );
                    }}
                  />
                </div>
              {:else}
                {cell.value}
              {/if}
            </svelte:fragment>
          </DataTable>
        </div>

        <div class="info-box" class:info-box--error={!duplicateLoginValid}>
          <InfoIcon size={18} />
          {#if duplicateLoginValid}
            All duplicate System Login Names have been corrected.
          {:else}
            <div>
              <div>
                Please correct the column "System Login Name" before continuing.
              </div>
              <ul class="duplicate-issue-list">
                {#each duplicateLoginIssues as issue}
                  <li>{issue.message}</li>
                {/each}
              </ul>
            </div>
          {/if}
        </div>

        <div class="duplicate-actions">
          <Button
            size="field"
            kind="secondary"
            text="Exclude rows & download Excel"
            iconLeft
            icon={DOWNLOAD_ICON}
            on:click={skipDuplicateLoginRowsAndSubmit}
          />
        </div>
      {:else}
        <div class="copy-block">
          <h3>Preview and import data</h3>
          <p>
            Review your data before importing. You can go back to adjust the
            column mapping if needed.
          </p>
        </div>

        <div class="preview-table-wrapper">
          <DataTable
            class="custom-table zebra-rows"
            headers={previewHeaders}
            rows={previewTableRows}
          />
        </div>

        <p class="meta-text info">
          Showing first {previewRows.length} rows of {parsedRows.length}
        </p>
        <div class="info-box">
          <InfoIcon size={18} />
          Existing users will be matched by System Login Name. Empty Excel cells
          should not overwrite existing user data when the backend import is added.
        </div>
      {/if}
    {/if}
  </div>
  <div slot="left-buttons">
    <Button
      text="Back"
      on:click={handleBack}
      variant="outline"
      iconLeft
      icon={ARROW_LEFT_DARK_ICON}
    />
  </div>
</Modal>

<style>
  .import-team-users-modal {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: fit-content;
    min-width: 46rem;
    max-width: calc(96vw - 4rem);
    padding-top: 0.25rem;
    font-family: Inter, sans-serif;
  }

  .steps {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr auto;
    align-items: center;
    gap: 0.75rem;
  }

  .steps--multiple-sheets {
    grid-template-columns: auto 1fr auto 1fr auto 1fr auto;
  }

  .step {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #a8a8a8;
    min-width: max-content;
  }

  .step--active {
    color: #161616;
  }

  .step__bullet {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    font-weight: 600;
    background: #e8e8e8;
    color: #6f6f6f;
    padding-top: 2px;
  }

  .step--active .step__bullet {
    background: #161616;
    color: #ffffff;
  }

  .step__label {
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .step__line {
    height: 1px;
    background: #d9d9d9;
    width: 100%;
  }

  .step--done {
    color: #161616;
  }

  .step--done .step__bullet {
    background: #161616;
    color: #ffffff;
  }

  .copy-block h3 {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #161616;
  }

  .copy-block p {
    margin: 0;
    font-size: 0.8125rem;
    color: #6f6f6f;
  }

  .dropzone-shell {
    position: relative;
    min-height: 12rem;
    border: 1px dashed #d9d9d9;
    border-radius: 0.25rem;
    background: #ffffff;
    overflow: hidden;
  }

  .dropzone-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: transparent;
    padding: 1.5rem;
  }

  .dropzone-title,
  .dropzone-separator {
    margin: 0;
    font-size: 0.875rem;
    color: #525252;
  }

  .dropzone-separator {
    font-size: 0.75rem;
    color: #8d8d8d;
  }

  .dropzone-filename {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #161616;
    margin-top: -1rem;
  }

  .meta-text {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    font-size: 0.75rem;
    color: #8d8d8d;
  }

  .meta-text.info {
    margin-top: -1rem;
  }

  .mapping-table {
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }

  .mapping-table__head,
  .mapping-table__row {
    display: grid;
    grid-template-columns: minmax(12rem, 18rem) minmax(20rem, 30rem) minmax(
        16rem,
        26rem
      );
    column-gap: 2rem;
    align-items: start;
  }

  .mapping-table__head {
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #525252;
    border-bottom: 1px solid #e0e0e0;
  }

  .mapping-table__row {
    padding: 1rem;
    border-bottom: 1px solid #e0e0e0;
  }

  .mapping-table__row:nth-child(odd) {
    background: #f4f4f4;
  }

  .mapping-table__row:nth-child(even) {
    background: #ffffff;
  }

  .mapping-table__field {
    font-size: 0.875rem;
    font-weight: 500;
    color: #161616;
  }

  .mapping-table__preview {
    font-size: 0.8125rem;
    color: #525252;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .drop-error {
    margin: 0;
    font-size: 0.75rem;
    color: #da1e28;
  }

  .required-note {
    margin: -0.5rem 0 0;
    font-size: 0.75rem;
    color: #6f6f6f;
  }

  .sheet-select {
    max-width: 24rem;
  }

  .info-box {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.375rem;
    background: #f8f8f8;
    font-size: 0.8125rem;
    color: #525252;
  }

  .info-box--error {
    border-color: #da1e28;
    color: #da1e28;
  }

  .duplicate-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: -0.5rem;
  }

  .preview-table-wrapper input {
    width: 100%;
    min-width: 9rem;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.25rem;
    font: inherit;
  }

  .preview-table-wrapper input.input-error {
    border-color: #da1e28;
  }

  .login-cell-state {
    margin: -0.25rem;
    padding: 0.25rem;
    border-radius: 0.25rem;
  }

  .login-cell-state--error {
    background: #e6a7ad;
  }

  .login-cell-state--valid {
    background: #b7e3bd;
  }

  .duplicate-issue-list {
    margin: 0.35rem 0 0;
    padding-left: 1.1rem;
  }

  .duplicate-issue-list li + li {
    margin-top: 0.2rem;
  }

  .selected-file-header {
    margin-left: 4rem;
  }

  .excel-icon {
    margin-top: -1rem;
  }

  :global(.import-team-users-modal .bx--file) {
    width: 100%;
  }

  :global(.import-team-users-modal .bx--file-container) {
    width: 100%;
  }

  :global(.import-team-users-modal .bx--file__drop-container) {
    min-height: 12rem;
    border: 0;
    background: transparent;
    justify-content: center;
    padding: 1.5rem;
  }

  :global(.import-team-users-modal .bx--file-browse-btn),
  :global(.import-team-users-modal .bx--label),
  :global(.import-team-users-modal .bx--file--label),
  :global(.import-team-users-modal .bx--file__selected-file) {
    display: none;
  }

  :global(.import-team-users-modal .bx--file-input) {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
  }

  .preview-table-wrapper {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
  }

  :global(.preview-table-wrapper .bx--data-table-container),
  :global(.preview-table-wrapper .bx--data-table-content),
  :global(.preview-table-wrapper .bx--data-table) {
    width: 100%;
  }

  :global(.dropzone-shell p) {
    padding-right: 0 !important;
  }

  :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td) {
    background-color: #ffffff !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table) {
    border-left: 1px solid #e0e0e0 !important;
    border-right: 1px solid #e0e0e0 !important;
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
  }

  :global(.import-team-users-modal-shell .bx--modal-container) {
    width: auto !important;
    max-width: 96vw !important;
  }

  :global(.import-team-users-modal-shell .modal_wrapper) {
    width: fit-content !important;
    max-width: calc(96vw - 2rem);
  }

  @media (max-width: 52rem) {
    .import-team-users-modal {
      width: auto;
      min-width: auto;
    }

    .steps {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .step__line {
      display: none;
    }
  }
</style>
